import { dirOf, isLocale, LOCALES, type Locale } from '@ly360/i18n';
import ar from '@ly360/i18n/locales/ar.json';
import en from '@ly360/i18n/locales/en.json';
import hi from '@ly360/i18n/locales/hi.json';
import ml from '@ly360/i18n/locales/ml.json';
import ta from '@ly360/i18n/locales/ta.json';
import tl from '@ly360/i18n/locales/tl.json';
import ur from '@ly360/i18n/locales/ur.json';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = { en, ar, hi, ml, ta, ur, tl };
const STORAGE_KEY = 'ly360.locale';

/** Pre-sign-in language: last choice on this device, else the browser language. After sign-in the server wins. */
function initialLocale(allowed: readonly Locale[]): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved) && allowed.includes(saved)) return saved;
  } catch {
    /* storage unavailable */
  }
  const nav = navigator.language.slice(0, 2);
  return isLocale(nav) && allowed.includes(nav) ? nav : 'en';
}

/** Sets <html lang/dir> so the whole document (scrollbars, form controls, logical CSS) mirrors. */
export function applyDocumentLocale(locale: Locale) {
  document.documentElement.lang = locale;
  document.documentElement.dir = dirOf(locale);
}

export function setLocale(locale: Locale) {
  void i18n.changeLanguage(locale);
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* storage unavailable */
  }
}

export function initI18n(allowed: readonly Locale[] = LOCALES) {
  void i18n.use(initReactI18next).init({
    resources: Object.fromEntries(Object.entries(resources).map(([k, v]) => [k, { translation: v }])),
    lng: initialLocale(allowed),
    fallbackLng: 'en',
    supportedLngs: [...allowed],
    interpolation: { escapeValue: false },
  });
  applyDocumentLocale(i18n.language as Locale);
  i18n.on('languageChanged', (l) => applyDocumentLocale(l as Locale));
  return i18n;
}

export { i18n };
